// src/XoneIsland.ts
var RUNTIME_KEY = "__xoneIslandRuntime";
var HYDRATED = /* @__PURE__ */ Symbol("xone-island-hydrated");
var ELEMENT_TAG = "xone-island";
var XoneIslandElement = class extends HTMLElement {
  [HYDRATED] = false;
  cleanup = [];
  connectedCallback() {
    if (this[HYDRATED]) return;
    const lazy = this.getAttribute("lazy") ?? "interaction";
    if (lazy === "eager") {
      void this.hydrate();
      return;
    }
    if (lazy === "visible") {
      this.observeVisible();
      return;
    }
    this.observeFirstInteraction();
  }
  disconnectedCallback() {
    for (const fn of this.cleanup) {
      try {
        fn();
      } catch {
      }
    }
    this.cleanup = [];
  }
  observeFirstInteraction() {
    const evts = [
      "focus",
      "click",
      "input",
      "change",
      "keydown"
    ];
    const onTrigger = () => {
      void this.hydrate();
      for (const e of evts) this.removeEventListener(e, onTrigger, true);
    };
    for (const e of evts) {
      this.addEventListener(e, onTrigger, { capture: true });
    }
  }
  observeVisible() {
    if (typeof IntersectionObserver === "undefined") {
      void this.hydrate();
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            obs.disconnect();
            void this.hydrate();
            return;
          }
        }
      },
      { rootMargin: "100px" }
    );
    obs.observe(this);
    this.cleanup.push(() => obs.disconnect());
  }
  async hydrate() {
    if (this[HYDRATED]) return;
    const runtime = window[RUNTIME_KEY];
    if (!runtime) {
      return;
    }
    try {
      this.wireListeners(runtime);
      this[HYDRATED] = true;
      this.setAttribute("hydrated", "");
    } catch (err) {
      try {
        runtime.api.console.error("[xone-island] hydrate failed", err);
      } catch {
      }
    }
  }
  wireListeners(runtime) {
    const eventsSpec = (this.getAttribute("data-events") ?? "").split(",").map((s) => s.trim()).filter((s) => s !== "");
    const propName = this.getAttribute("data-prop") ?? "";
    const propType = (this.getAttribute("data-type") ?? "").toUpperCase();
    for (const spec of eventsSpec) {
      if (spec === "bind") {
        this.wireBind(runtime, propName, propType);
      } else if (spec === "click") {
        this.wireClickInline(runtime, propName);
      } else if (spec === "frame-click") {
        this.wireFrameClick(runtime, propName);
      } else if (spec === "change") {
        this.wireChangeInline(runtime, propName);
      } else if (spec === "contents") {
        this.wireContents(runtime, propName);
      } else if (spec.startsWith("method:")) {
        const methodName = spec.slice("method:".length);
        this.wireMethod(runtime, propName, methodName);
      }
    }
  }
  wireBind(runtime, propName, propType) {
    if (propName === "") return;
    const input = this.querySelector(
      "input,textarea,select"
    );
    if (!input) return;
    const initial = safeGetValue(runtime.api, propName);
    if (initial !== void 0 && initial !== null) {
      if (propType === "NC" && "checked" in input) {
        input.checked = Boolean(initial);
      } else {
        input.value = String(initial);
      }
    }
    const onInput = () => {
      const v = propType === "NC" && "checked" in input ? input.checked : input.value;
      try {
        void runtime.api.self.setValue(propName, v);
      } catch (err) {
        runtime.api.console.error("[xone-island] bind setValue", err);
      }
      const changeHandler = runtime.events[`${propName}_onchange`];
      if (typeof changeHandler === "function") {
        void invokeHandler(runtime, changeHandler, [v]);
      }
    };
    const ev = propType === "NC" ? "change" : "input";
    input.addEventListener(ev, onInput);
    this.cleanup.push(() => input.removeEventListener(ev, onInput));
  }
  wireClickInline(runtime, propName) {
    const handlerName = `${propName}_onclick`;
    const handler = runtime.events[handlerName];
    if (typeof handler !== "function") {
      return;
    }
    const button = this.querySelector("button");
    const target = button ?? this;
    const onClick = (e) => {
      void invokeHandler(runtime, handler, [e]);
    };
    target.addEventListener("click", onClick);
    this.cleanup.push(() => target.removeEventListener("click", onClick));
  }
  wireFrameClick(runtime, frameName) {
    const handlerName = `${frameName}_onclick`;
    const handler = runtime.events[handlerName];
    if (typeof handler !== "function") return;
    const frame = this.querySelector(`[data-xone-frame="${frameName}"]`) ?? this;
    frame.style.cursor = "pointer";
    const onClick = (e) => {
      e.stopPropagation();
      void invokeHandler(runtime, handler, [e]);
    };
    frame.addEventListener("click", onClick);
    this.cleanup.push(() => frame.removeEventListener("click", onClick));
  }
  wireChangeInline(runtime, propName) {
    const handlerName = `${propName}_onchange`;
    const handler = runtime.events[handlerName];
    if (typeof handler !== "function") return;
    const input = this.querySelector("input,textarea,select");
    if (!input) return;
    const onChange = () => {
      void invokeHandler(runtime, handler, [input.value]);
    };
    input.addEventListener("change", onChange);
    this.cleanup.push(() => input.removeEventListener("change", onChange));
  }
  wireMethod(runtime, propName, methodName) {
    const handler = runtime.events[methodName];
    if (typeof handler !== "function") return;
    const button = this.querySelector("button");
    const target = button ?? this;
    const onClick = () => {
      void invokeHandler(runtime, handler, []);
    };
    target.addEventListener("click", onClick);
    this.cleanup.push(() => target.removeEventListener("click", onClick));
    void propName;
  }
  wireContents(runtime, propName) {
    let srcColl = this.getAttribute("data-src") ?? "";
    let filter = this.getAttribute("data-filter") ?? void 0;
    let sort = this.getAttribute("data-sort") ?? void 0;
    const innerDiv = this.querySelector("div[data-src]");
    if (!srcColl && innerDiv) {
      srcColl = innerDiv.getAttribute("data-src") ?? "";
      filter = filter ?? innerDiv.getAttribute("data-filter") ?? void 0;
      sort = sort ?? innerDiv.getAttribute("data-sort") ?? void 0;
    }
    if (!srcColl) {
      this.setAttribute("data-status", "no-src");
      return;
    }
    const container = innerDiv ?? this;
    const templateEl = container.querySelector(
      "template[data-item-template]"
    );
    const templateHtml = templateEl?.innerHTML ?? "";
    const viewmode = (container.getAttribute("data-viewmode") ?? "recyclerview").toLowerCase();
    const columns = parseInt(container.getAttribute("data-columns") ?? "2", 10);
    const xorientation = (container.getAttribute("data-xorientation") ?? "vertical").toLowerCase();
    const cellHeight = container.getAttribute("data-cell-height") ?? "";
    const showNoData = container.getAttribute("data-show-no-data") ?? "true";
    const bgcolor = container.getAttribute("data-bgcolor") ?? "";
    this.setAttribute("data-status", "loading");
    const resolvedFilter = this.resolveMacros(runtime, filter);
    void (async () => {
      try {
        const coll = runtime.api.appData.getCollection(srcColl);
        await coll.loadAll(resolvedFilter, sort);
        const count = coll.getCount?.() ?? 0;
        container.innerHTML = "";
        this.applyContentsLayout(container, viewmode, columns, xorientation, bgcolor);
        if (count === 0) {
          if (showNoData.toLowerCase() !== "false") {
            const empty = document.createElement("div");
            empty.className = "xone-contents-empty";
            empty.textContent = "No hay datos";
            container.appendChild(empty);
          }
          this.setAttribute("data-status", "empty");
          return;
        }
        if (coll.startBrowse) {
          await coll.startBrowse();
          let idx = 0;
          while (coll.isEOF && !coll.isEOF()) {
            const item = coll.getCurrentItem?.();
            if (item) {
              this.appendContentRow(container, templateHtml, item, idx, cellHeight);
            }
            if (coll.moveNext) await coll.moveNext();
            else break;
            idx++;
          }
          coll.endBrowse?.();
        } else {
          for (let i = 0; i < count; i++) {
            const item = coll.get?.(i);
            if (item) {
              this.appendContentRow(container, templateHtml, item, i, cellHeight);
            }
          }
        }
        this.setAttribute("data-status", "loaded");
        this.wireSelectedItem(runtime, propName, container);
      } catch (err) {
        this.setAttribute("data-status", "error");
        try {
          runtime.api.console.error("[xone-island] contents load failed", err);
        } catch {
        }
        const errObj = err;
        const status = errObj?.status;
        const code = errObj?.code ?? "";
        container.innerHTML = "";
        const placeholder = document.createElement("div");
        placeholder.className = "xone-contents-error";
        placeholder.style.cssText = "padding:16px;text-align:center;color:#999;font-size:13px;";
        if (status === 401 || code.includes("AUTH")) {
          placeholder.textContent = "Inicia sesi\xF3n para ver este contenido";
        } else if (status === 403) {
          placeholder.textContent = "No tienes permisos para ver este contenido";
        } else if (status === 404) {
          placeholder.textContent = "Contenido no disponible";
        } else {
          placeholder.textContent = "Error al cargar datos";
        }
        container.appendChild(placeholder);
      }
    })();
  }
  applyContentsLayout(container, viewmode, columns, xorientation, bgcolor) {
    container.classList.add("xone-contents");
    if (viewmode === "gridview") {
      container.classList.add("is-gridview");
      container.style.display = "grid";
      container.style.gridTemplateColumns = `repeat(${columns > 0 ? columns : 2}, 1fr)`;
      container.style.gap = "8px";
    } else {
      container.classList.add("is-recyclerview");
      container.style.display = "flex";
      if (xorientation === "horizontal") {
        container.style.flexDirection = "row";
        container.style.overflowX = "auto";
        container.style.overflowY = "hidden";
        container.style.scrollSnapType = "x mandatory";
        container.dataset["orientation"] = "horizontal";
      } else {
        container.style.flexDirection = "column";
        container.dataset["orientation"] = "vertical";
      }
      container.style.gap = "6px";
    }
    if (bgcolor) {
      container.style.padding = viewmode === "gridview" ? "8px" : "6px";
    }
    const existingHeight = container.style.height;
    if (existingHeight && existingHeight !== "auto" && !existingHeight.includes("-2")) {
      container.style.overflowY = "auto";
    }
  }
  appendContentRow(container, templateHtml, item, index, cellHeight) {
    const row = document.createElement("article");
    row.className = "xone-content-item xone-contents-row";
    row.innerHTML = templateHtml;
    if (cellHeight && cellHeight !== "-2") {
      const numMatch = cellHeight.match(/^(\d+)p$/);
      if (numMatch) {
        row.style.height = `calc(${numMatch[1]} * var(--xone-p-unit, 1px))`;
        row.style.flexShrink = "0";
        row.style.overflow = "hidden";
      }
    }
    const propEls = row.querySelectorAll("[data-prop]");
    for (const el of propEls) {
      const field = el.getAttribute("data-prop");
      if (!field) continue;
      const value = item[field];
      if (value === void 0 || value === null) continue;
      const str = String(value);
      const labelEl = el.querySelector(".xone-label-text");
      if (labelEl) {
        labelEl.textContent = str;
        continue;
      }
      const textEl = el.querySelector(".xone-text-value");
      if (textEl) {
        textEl.textContent = str;
        continue;
      }
      const img = el.tagName === "IMG" ? el : el.querySelector("img");
      if (img && str) {
        img.src = str;
        continue;
      }
      const input = el.querySelector("input,textarea");
      if (input) {
        input.value = str;
        continue;
      }
      if (el.tagName === "SPAN" || el.tagName === "DIV") {
        el.textContent = str;
      }
    }
    row.setAttribute("data-row-index", String(index));
    container.appendChild(row);
  }
  resolveMacros(runtime, filter) {
    if (!filter || !filter.includes("##")) return filter;
    const macroPattern = /##([A-Za-z0-9_]+)##/g;
    const appData = runtime.api.appData;
    const self = runtime.api.self;
    let result = filter;
    if (typeof appData.expandMacros === "function") {
      result = appData.expandMacros(result);
    }
    if (result.includes("##")) {
      result = result.replace(macroPattern, (_match, name) => {
        try {
          if ((name.startsWith("FLD_") || name.startsWith("OWN_")) && self) {
            const fieldName = name.startsWith("FLD_") ? name.slice(4) : name.slice(4);
            const val = typeof self.getValue === "function" ? self.getValue(fieldName) : self[fieldName];
            if (val !== void 0 && val !== null) return String(val);
            if (typeof appData.getGlobalMacro === "function") {
              const macroVal = appData.getGlobalMacro(fieldName);
              if (macroVal !== void 0 && macroVal !== null) return String(macroVal);
            }
          }
          if (typeof appData.getGlobalMacro === "function") {
            const val = appData.getGlobalMacro(name);
            if (val !== void 0 && val !== null) return String(val);
          }
        } catch {
        }
        return `##${name}##`;
      });
    }
    return result;
  }
  wireSelectedItem(runtime, propName, container) {
    const contentsName = this.getAttribute("data-contents-name") ?? "";
    const sanitized = contentsName.replace(/^@/, "_");
    const handler = (sanitized ? runtime.events[`${sanitized}_selecteditem`] : void 0) ?? runtime.events[`${propName}_selecteditem`] ?? runtime.events["selecteditem"];
    if (typeof handler !== "function") return;
    const srcColl = this.getAttribute("data-src") ?? "";
    const target = container ?? this;
    const onClick = (e) => {
      const evTarget = e.target;
      const row = evTarget.closest?.(".xone-contents-row");
      if (!row) return;
      const index = Number(row.getAttribute("data-row-index") ?? "0");
      void (async () => {
        try {
          let itemSelf = void 0;
          if (srcColl) {
            const coll = runtime.api.appData.getCollection(srcColl);
            itemSelf = coll?.get?.(index);
          }
          if (itemSelf && typeof itemSelf === "object") {
            const itemApi = {
              ...runtime.api,
              self: itemSelf,
              This: itemSelf
            };
            await handler(itemApi, index);
          } else {
            await handler(runtime.api, index);
          }
        } catch (err) {
          try {
            runtime.api.console.error("[xone-island] selecteditem error", err);
          } catch {
          }
        }
      })();
    };
    target.addEventListener("click", onClick);
    this.cleanup.push(() => target.removeEventListener("click", onClick));
    void propName;
  }
};
function safeGetValue(api, prop) {
  try {
    return api.self.getValue(prop);
  } catch {
    return void 0;
  }
}
async function invokeHandler(runtime, handler, args) {
  try {
    await handler(runtime.api, ...args);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    try {
      runtime.api.console.error("[xone-island] handler error:", msg, err);
    } catch {
      console.error("[xone-island] handler error:", err);
    }
    try {
      const ui = runtime.api.ui;
      ui?.showToast?.(`Error: ${msg}`);
    } catch {
    }
  }
}
var elementRegistered = false;
function registerXoneIsland(opts) {
  if (typeof window === "undefined") {
    return;
  }
  window[RUNTIME_KEY] = { api: opts.api, events: opts.events };
  if (elementRegistered) return;
  elementRegistered = true;
  if (typeof customElements !== "undefined" && !customElements.get(ELEMENT_TAG)) {
    customElements.define(ELEMENT_TAG, XoneIslandElement);
  }
}
function _resetXoneIslandForTests() {
  if (typeof window !== "undefined") {
    delete window[RUNTIME_KEY];
  }
  elementRegistered = false;
}
export {
  XoneIslandElement,
  _resetXoneIslandForTests,
  registerXoneIsland
};
//# sourceMappingURL=index.js.map